// sendOps — a list of edit ops as ONE save of the queue: each op's port call
// in turn, each guarded by the hash the one before returned. An element an op
// adds (its insert returns a path) has its path for the ops after it. An op
// whose element has no path refuses the save, as `atPaths` does.

export type { EditPorts };
export { sendOps };

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { mkRejected } from "../../app/externalError.js";
import type { PortResult } from "../../app/ports/result.js";
import type { AppPorts } from "../../app/ports.js";
import type { ElementId } from "../Editor/types.js";
import type { Paths } from "./saveQueue.js";
import type { UndoOp } from "./undoSteps.js";
import type { StructuralRun } from "./useSaveQueue.js";

type EditPorts = Pick<
    AppPorts,
    | "updateElementText"
    | "patchElementAttrs"
    | "retypeElement"
    | "insertBlock"
    | "addChoice"
    | "removeElement"
    | "moveElement"
>;

type Sent = { readonly contentHash: string; readonly path?: readonly string[] };

// The elements whose paths an op is sent at.
const pathsNeeded = (op: UndoOp): readonly ElementId[] => {
    switch (op.type) {
        case "append-block":
            return [];
        case "append-choice":
            return [op.mcqId];
        case "move":
            return [op.id, op.anchor];
        case "remove":
        case "text":
        case "attrs":
        case "retype":
            return [op.id];
        default:
            return exhaustSwitchCase(op);
    }
};

const sendOp = (
    ports: EditPorts,
    materialId: string,
    hash: string,
    paths: Paths,
    op: UndoOp,
): PortResult<Sent> => {
    if (!pathsNeeded(op).every((id) => paths.has(id)))
        return Promise.resolve(Result.err(mkRejected("no path for the edit")));
    const at = (id: ElementId) => paths.get(id) ?? [];
    switch (op.type) {
        case "append-block":
            return ports.insertBlock(materialId, op.block, hash);
        case "append-choice":
            return ports.addChoice(materialId, at(op.mcqId), op.choice, hash);
        case "remove":
            return ports.removeElement(materialId, at(op.id), hash);
        case "move":
            return ports.moveElement(
                materialId,
                at(op.id),
                at(op.anchor),
                op.position,
                hash,
            );
        case "text":
            return ports.updateElementText(
                materialId,
                at(op.id),
                op.text,
                hash,
                op.base,
            );
        case "attrs":
            return ports.patchElementAttrs(
                materialId,
                at(op.id),
                op.attrs,
                hash,
            );
        case "retype":
            return ports.retypeElement(materialId, at(op.id), op.to, hash);
        default:
            return exhaustSwitchCase(op);
    }
};

const sendOps =
    (ports: EditPorts, ops: readonly UndoOp[]): StructuralRun["persist"] =>
    (materialId, hash, paths) =>
        ops.reduce<PortResult<Sent & { readonly paths: Paths }>>(
            async (before, op) => {
                const r = await before;
                if (Result.isErr(r)) return r;
                const sent = await sendOp(
                    ports,
                    materialId,
                    r.value.contentHash,
                    r.value.paths,
                    op,
                );
                if (Result.isErr(sent)) return sent;
                const minted = sent.value.path;
                const id = minted?.at(-1);
                return Result.ok({
                    contentHash: sent.value.contentHash,
                    path: minted ?? r.value.path,
                    paths:
                        minted === undefined || id === undefined
                            ? r.value.paths
                            : new Map([...r.value.paths, [id, minted]]),
                });
            },
            Promise.resolve(Result.ok({ contentHash: hash, paths })),
        );
